import { getStorageSession, registerStorageResponse } from './storageUrls.js';
/**
 * VisionProjects gRPC adapter — project CRUD + per-project location CRUD.
 *
 * Presents the legacy dashboard + artboard/sidebar shapes (a `{ projects, hasMore,
 * nextPageToken }` page, a single legacy `project`, and the legacy `category` shape for
 * each location) on top of the `protos.vision.projects.v1.VisionProjects` gRPC-web
 * service, so the UI is unaware of the transport.
 */

import { createClient } from '@connectrpc/connect';
import { createGrpcWebTransport } from '@connectrpc/connect-web';
import { VisionProjects } from '../gen/vision/projects/v1/projects_service_pb';
import {
  listResponseToProjects,
  createdProjectToProject,
  detailResponseToLoadProjectShape,
} from './visionProjectsMapping';
import {
  listLocationsResponseToCategories,
  createdLocationIdToCategory,
} from './visionLocationsMapping';
import { config } from '../config';
import { getIdTokenForRequest } from './firebaseClient.js';

const BASE_URL = config.VISION_GRPC_BASE_URL;

const PAGE_SIZE = 30;

const authInterceptor = (next) => async (req) => {
  const token = await getIdTokenForRequest();
  if (token) {
    req.header.set('authorization', `Bearer ${token}`);
  }
  const session = getStorageSession();
  const response = await next(req);
  registerStorageResponse(response.message, session);
  return response;
};

function buildClient({ keepalive = false } = {}) {
  const transport = createGrpcWebTransport({
    baseUrl: BASE_URL,
    interceptors: [authInterceptor],
    ...(keepalive ? { fetch: (input, init) => fetch(input, { ...init, keepalive: true }) } : {}),
  });

  return createClient(VisionProjects, transport);
}

let cachedClient = null;

function client() {
  if (!cachedClient) cachedClient = buildClient();
  return cachedClient;
}

export async function listMyProjects({
  pageToken,
  includeLocationSummaries = false,
  includeCoverThumbnail = false,
  includeTotalImageCount = false,
  includeGenerationCount = false,
} = {}) {
  const pagination = { pageSize: PAGE_SIZE };
  if (pageToken) {
    pagination.pageToken = { case: 'nextToken', value: pageToken };
  }
  const response = await client().listMyVisionProjects({
    params: {
      includeLocationSummaries,
      includeCoverThumbnail,
      includeTotalImageCount,
      includeGenerationCount,
    },
    pagination,
  });
  return listResponseToProjects(response);
}

export async function createProject({ name, description }) {
  const response = await client().createVisionProject({ params: { name, description } });
  if (!response.result?.project) {
    throw new Error('CreateVisionProject returned no project');
  }
  return createdProjectToProject(response.result.project);
}

export async function updateProject({ projectId, name, description }) {
  const trimmedName = name.trim();
  const trimmedDescription = (description || '').trim();
  await client().updateVisionProject({
    params: { projectId, name: trimmedName, description: trimmedDescription },
    updateMask: { paths: ['params.name', 'params.description'] },
  });
  return { name: trimmedName, description: trimmedDescription };
}

export async function deleteProject({ projectId }) {
  await client().deleteVisionProject({ params: { projectId } });
}

function presenceRequestParams({
  projectId,
  kind,
  imageId,
  generationId,
  gradingLockGenerationId,
  cropLockGenerationId,
}) {
  return {
    projectId,
    kind,
    imageId: imageId || '',
    generationId: generationId || '',
    gradingLockGenerationId: gradingLockGenerationId || '',
    cropLockGenerationId: cropLockGenerationId || '',
  };
}

export async function trackVisionPresence(params) {
  await client().trackVisionPresence({ params: presenceRequestParams(params) });
}

// A keepalive-fetch transport, used only for the LEAVE sent during page unload (tab/window
// close): a normal request is cancelled when the document tears down, whereas a keepalive
// request is allowed to complete, so peers evict instantly instead of waiting out the TTL.
let cachedUnloadClient = null;

function unloadClient() {
  if (!cachedUnloadClient) cachedUnloadClient = buildClient({ keepalive: true });
  return cachedUnloadClient;
}

export async function trackVisionPresenceUnload(params) {
  await unloadClient().trackVisionPresence({ params: presenceRequestParams(params) });
}

export async function getProject({ projectId, include } = {}) {
  const wantArtboard = include === 'artboard';
  const response = await client().getVisionProject({
    params: {
      projectId,
      includeLocations: true,
      includeCast: wantArtboard,
      includeProps: wantArtboard,
      includeShotlistConfig: wantArtboard,
      includeShotlistCustomPresets: wantArtboard,
    },
  });
  return detailResponseToLoadProjectShape(response);
}

export async function listLocations(projectId) {
  const response = await client().listVisionLocations({ params: { projectId } });
  return listLocationsResponseToCategories(response, projectId);
}

export async function createLocation(projectId, { name, address = '', sortOrder }) {
  const response = await client().createVisionLocation({
    params: { projectId, name, address },
  });
  if (!response.result?.locationId) {
    throw new Error('CreateVisionLocation returned no location_id');
  }
  return createdLocationIdToCategory({
    locationId: response.result.locationId,
    projectId,
    name,
    address,
    sortOrder,
  });
}

export async function updateLocation(projectId, locationId, { name, address } = {}) {
  const paths = [];
  const params = { projectId, locationId, name: '', address: '' };

  if (name !== undefined) {
    paths.push('params.name');
    params.name = name;
  }
  if (address !== undefined) {
    paths.push('params.address');
    params.address = address;
  }

  if (paths.length === 0) {
    throw new Error('updateLocation: at least one of name/address must be provided');
  }

  await client().updateVisionLocation({ params, updateMask: { paths } });
}

export async function deleteLocation(projectId, locationId) {
  await client().deleteVisionLocation({ params: { projectId, locationId } });
}

/** Update (or clear) the Vision Board generation rules for a project. Pass an empty/null
 * `rules` value to clear them. */
export async function updateVisionBoardRules({ projectId, rules }) {
  await client().updateVisionBoardRules({
    params: { projectId, visionBoardRules: rules || '' },
    updateMask: { paths: ['params.vision_board_rules'] },
  });
}
